import Cookies from "js-cookie";
import React, { useCallback, useEffect, useState, useRef } from "react";

import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import {
  MarketingFieldBlurredEvent,
  MarketingFieldFocusedEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";

import { usePartnerPrefillFromSearchParams } from "../../../routes/signup/Form";
import { getLead } from "../../modules/FormMultistep/utils";

import { FieldState, SetFields, FormStatus, SubmitError, Field } from "./types";
import { validateField } from "./utils";

type SubmitArgs = {
  event: React.FormEvent;
  fieldState: FieldState;
};

export const useInput = (
  setFields: SetFields,
  formId: number,
  field: Field,
) => {
  const { trackEvent } = useAnalytics();
  const onInputChange = React.useCallback(
    ({ name, value }) => {
      setFields((fields: FieldState) => ({
        ...fields,
        [name]: {
          ...fields[name],
          error: validateField({
            fieldValue: value,
            isRequired: field.required,
            type: field.dataType,
          }),
          value,
        },
      }));
    },
    [field.dataType, field.required, setFields],
  );

  const onCheckboxChange = React.useCallback(
    ({ name, value, checkBox }) => {
      //removing checkbox value if exists, adding checkbox value if it doesn't
      //value array changes in place
      const checkBoxIndex = value.indexOf(checkBox.value);
      if (checkBoxIndex > -1) value.splice(checkBoxIndex, 1);
      else value.push(checkBox.value);
      //
      //reshaped into value = [] since the api needs an array of checked boxes.
      setFields((fields: FieldState) => ({
        ...fields,
        [name]: {
          ...fields[name],
          error: validateField({
            fieldValue: value,
            isRequired: field.required,
            type: field.dataType,
          }),
          value,
        },
      }));
    },
    [field.dataType, field.required, setFields],
  );

  const onInputBlur = () => {
    trackEvent(new MarketingFieldBlurredEvent(field.name, formId));
  };

  const onInputFocus = () => {
    trackEvent(new MarketingFieldFocusedEvent(field.name, formId));
  };

  return {
    onInputChange,
    onInputBlur,
    onInputFocus,
    onCheckboxChange,
  };
};

export const useSubmit = (setFormStatus: (status: FormStatus) => void) => {
  const [submitError, setFormError] = React.useState<SubmitError>();

  const submit = React.useCallback(
    ({
      event,
      fieldState,
    }: SubmitArgs): {
      hasErrors: boolean;
    } => {
      event.preventDefault();
      setFormStatus(FormStatus.SUBMIT_ATTEMPTED);
      const hasErrors = Object.values(fieldState).some(({ error }) => !!error);

      if (hasErrors) {
        setFormError({
          title: "Missing information",
          message:
            "Complete the required fields and correct any errors to continue.",
        });
        return {
          hasErrors,
        };
      }

      setFormStatus(FormStatus.SUBMITTING);
      return { hasErrors };
    },
    [setFormStatus],
  );

  return {
    submit,
    submitError,
    setFormError,
  };
};

// Mapping function to convert lead object to form field values
const mapLeadToFormFields = (leadDetails: any): Record<string, any> => {
  if (!leadDetails) return {};

  const mapping: Record<string, any> = {};

  // Map lead fields to form fields
  if (leadDetails.firstName) {
    mapping.FirstName = leadDetails.firstName;
  }

  if (leadDetails.lastName) {
    mapping.LastName = leadDetails.lastName;
  }

  if (leadDetails.email) {
    mapping.Email = leadDetails.email;
  }

  if (leadDetails.phoneNumber) {
    mapping.Phone = leadDetails.phoneNumber;
  }

  if (leadDetails.companyName) {
    mapping.Company = leadDetails.companyName;
  }

  return mapping;
};

export function usePrefillFromLead(
  prefillFromLead?: boolean | null,
  search?: string,
) {
  const leadToken = Cookies.get("leadToken");
  const [leadDetails, setLeadDetails] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const hasAttemptedFetch = useRef(false);

  const { userPrefillData } = usePartnerPrefillFromSearchParams(search);

  const handleGetLead = useCallback(async () => {
    if (!prefillFromLead || hasAttemptedFetch.current) {
      return;
    }

    if (leadToken && !userPrefillData?.attributionToken) {
      setIsLoading(true);
      hasAttemptedFetch.current = true;

      try {
        const { leadDetails } = await getLead(leadToken);
        setLeadDetails(leadDetails);
      } catch (error) {
        console.error("Error fetching lead details:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [leadToken, userPrefillData?.attributionToken, prefillFromLead]);

  useEffect(() => {
    if (!prefillFromLead) {
      return;
    }

    const params = new URLSearchParams(search);
    const prefToken = params.get("pref_token") as string;
    if (prefToken) {
      setLeadDetails(userPrefillData);
    } else {
      handleGetLead();
    }
  }, [handleGetLead, search, prefillFromLead, userPrefillData]);

  // Return the prefill data mapped to form fields
  const prefillData = React.useMemo(() => {
    if (!leadDetails) return null;
    return mapLeadToFormFields(leadDetails);
  }, [leadDetails]);

  return {
    isLoading,
    prefillData,
  };
}
